/*
 * SkillzSDK-iOS
 *
 * Created by Theo Tsapakos on 8/9/16.
 *
 * @providesModule RatingModal
 *
 */
import React from 'react';

import AWSManager from 'AWSManager';
import CurrentUserStore from 'CurrentUserStore';
import FTUEStyles from 'FTUEStyles';
import INTL from 'INTL';
import Label from 'Label';
import LoadingIndicator from 'LoadingIndicator';
import ModalNavigationController from 'ModalNavigationController';
import NetClient from 'NetClient';
import OrientationTransitionController from 'OrientationTransitionController';
import PushButton from 'PushButton';
import RatingPromptController from 'RatingPromptController';
import Sizes from 'Sizes';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import styles from 'RatingModalStyles';

import {NativeModules, Platform, Pressable, TextInput, View} from 'react-native';
import SKZLayoutAnimations from 'SKZLayoutAnimations';

const SKZReactVCManager = NativeModules.SKZReactViewControllerManager;
const SKZAPIManager = NativeModules.SKZAPIManager;
const SkillzModule = NativeModules.SkillzModule;
interface IProps {}

interface IState {
  userInput?: string;
  lastPressedStarIndex: number;
  hasRated: boolean;
  lessThanFourStars: boolean;
  userId?: string;
  isLandscape: boolean;
}

export default class RatingModal extends React.Component<IProps, IState> {
  textInput?: TextInput = undefined;
  lastPressedStarIndex: number = 0;
  hasRated: boolean = false;
  lessThanFourStars: boolean = false;

  constructor(props: IProps) {
    super(props);
    this.state = {
      userInput: '',
      lastPressedStarIndex: -1,
      hasRated: this.lastPressedStarIndex > 0 ? true : false,
      lessThanFourStars: this.hasRated ? (this.lastPressedStarIndex < 3 ? true : false) : false,
      isLandscape: false,
      userId: undefined,
    };
  }

  componentDidMount() {
    LoadingIndicator.show();
    setTimeout(() => {
      this._getInitialState('PORTRAIT');
      OrientationTransitionController.getCurrentOrientation((orientation: string) => {
        this._getInitialState(orientation);
      });
    }, 300);
  }

  _getInitialState(currentOrientation: string) {
    CurrentUserStore.getCurrentUserId().then((userId: string) => {
      this.setState({userId: userId, isLandscape: currentOrientation == 'LANDSCAPE'}, () => {
        LoadingIndicator.hide();
      });
    });
  }

  _handleCancelButton() {
    this._recordSubmissionForAWS('RATING_PROMPT_RESPONSE_ML');
    this._dismissModal(true);
  }

  _handleSubmitButton() {
    if (this.state.hasRated) {
      this._recordSubmissionForAWS('RATING_PROMPT_RESPONSE_SUBMIT');
      this._dismissModal(false);
      if (!this.state.lessThanFourStars) {
        RatingPromptController.showAppStoreRatingPrompt();
      } else {
        this._submitReview();
      }
    }
  }

  _setTextInputRef = (element: TextInput) => {
    this.textInput = element;
  };

  _focusTextInput = () => {
    if (this.textInput) {
      this.textInput.focus();
    }
  };

  _submitReview() {
    RatingPromptController.promptUserNextVerison();
    if (Platform.OS === 'ios') {
      NetClient.fetchURL(
        'POST',
        'users/' + this.state.userId + '/support_tickets',
        {
          subject: 'Rating Feedback - ' + (this.state.lastPressedStarIndex + 1) + ' Stars',
          message: this.state.userInput,
        },
        null,
        SKZAPIManager.OnError.failSilently
      )
        ?.then((payload) => {
          console.warn('Review Sent', payload);
        })
        .catch((error) => {
          console.warn('Failed to send review', error);
        });
    } else {
      // Feature not implemented on Android?
    }
  }

  _dismissModal(shouldDelay: boolean) {
    if (RatingPromptController.shouldUseOTARatingPrompt()) {
      ModalNavigationController.hideModal();
      RatingPromptController.didDismissRatingModal();
    } else {
      SKZReactVCManager.dismissLastPresentedController(true, () => {});
      SkillzModule.setEnabledKeyboardManager(false);
    }
    if (shouldDelay) {
      RatingPromptController.remindLater();
    }
  }

  _recordSubmissionForAWS(response: string) {
    AWSManager.trackEvent(response, {stars: this.state.lastPressedStarIndex + 1});
  }

  _renderButtons(): React.ReactNode {
    if (!this.state.hasRated) {
      return;
    }

    const cancelButtonText = this.state.lessThanFourStars
      ? INTL.message('CANCEL')
      : INTL.message('MAYBE LATER');
    return (
      <View style={{flexDirection: 'row', marginBottom: 12}}>
        <PushButton
          colors={SkillzStyle.gradients.btnGradientThree}
          cornerRadius={SkillzStyle.fullStyles.viewCornerRadiusPrimary.borderRadius}
          onPress={() => {
            this._handleCancelButton();
          }}
          style={styles.buttonComponent}
          textStyle={styles.buttonText}
          title={cancelButtonText}
        />
        <PushButton
          colors={SkillzStyle.gradients.btnGradientOne}
          cornerRadius={SkillzStyle.fullStyles.viewCornerRadiusPrimary.borderRadius}
          onPress={() => {
            this._handleSubmitButton();
          }}
          style={styles.buttonComponent}
          textStyle={styles.buttonText}
          title={INTL.message('SUBMIT')}
        />
      </View>
    );
  }

  _renderInputSection(): React.ReactNode {
    if (this.state.lessThanFourStars) {
      const height = !this.state.isLandscape
        ? 150
        : Sizes.dynamic({
            sm: 60,
            lg: 80,
          });

      return (
        <View style={[styles.userInputContainer, {height: height}]}>
          <TextInput
            style={styles.inputSectionText}
            ref={this._setTextInputRef}
            autoCorrect={true}
            onChangeText={(input: string) => {
              this.setState({userInput: input});
            }}
            returnKeyType='done'
            blurOnSubmit={true}
            placeholder={INTL.message('Write to Skillz support here...')}
            placeholderTextColor={SkillzStyle.fullStyles.textColorTertiary.color}
            defaultValue={this.state.userInput}
            multiline={true}
            key={'textField'}
            underlineColorAndroid={'transparent'}
          />
        </View>
      );
    }
  }

  _renderInstructionText(): React.ReactNode {
    let text = '';
    let containerStyle = {};
    if (this.state.lastPressedStarIndex == -1) {
      // eslint-disable-line no-magic-numbers
      text = INTL.message('Tap a star to rate');
      containerStyle = {
        paddingTop: Sizes.orientation({portrait: 18, landscape: 10}),
        paddingBottom: Sizes.orientation({portrait: 0, landscape: 0}),
      };
    } else if (this.state.lessThanFourStars) {
      text = INTL.message('Sorry to hear that. How can we improve?');
      containerStyle = {
        paddingVertical: Sizes.orientation({portrait: 12, landscape: 6}),
      };
    } else {
      return <View style={{paddingVertical: Sizes.orientation({portrait: 12, landscape: 5})}} />;
    }

    return (
      <View style={containerStyle}>
        <Label
          style={
            Platform.OS === 'android' ? styles.instructionsTextAndroid : styles.instructionsText
          }
        >
          {text}
        </Label>
      </View>
    );
  }

  _starPressed(index: number) {
    const lessThanFourStars = index < 3 ? true : false; // eslint-disable-line no-magic-numbers
    if (this.state?.userInput?.length == 0) {
      this.setState({
        lastPressedStarIndex: index,
        lessThanFourStars: lessThanFourStars,
        hasRated: true,
      });
    }
  }

  _renderStar(index: number): React.ReactNode {
    return (
      /* eslint-disable no-magic-numbers */
      <View style={styles.starContainer}>
        <Pressable
          style={{height: 45, width: 45}}
          onPress={() => {
            this._starPressed(index);
          }}
        >
          <SkillzImage
            source={'ic_rating_star_skz.png'}
            style={{height: 40, width: 40}}
            key={index + ' ' + (this.state.lastPressedStarIndex >= index)}
            resizeMode='contain'
            color={
              this.state.lastPressedStarIndex >= index
                ? '#FDC229'
                : SkillzStyle.convertToOpacity('#000000', 0.4)
            }
          />
        </Pressable>
      </View>
      /* eslint-enable no-magic-numbers */
    );
  }

  _renderStars(): React.ReactNode {
    return (
      /* eslint-disable no-magic-numbers */
      <View style={styles.starsContainer}>
        {this._renderStar(0)}
        {this._renderStar(1)}
        {this._renderStar(2)}
        {this._renderStar(3)}
        {this._renderStar(4)}
      </View>
      /* eslint-enable no-magic-numbers */
    );
  }

  _renderHeader(): React.ReactNode {
    return (
      <View style={styles.headerContainer}>
        <View style={{paddingTop: 10}}>
          <Label style={Platform.OS === 'android' ? styles.titleTextAndroid : styles.titleText}>
            {INTL.message('Enjoying the competition?')}
          </Label>
        </View>
        <View style={{paddingTop: 10}}>
          <Label
            style={Platform.OS === 'android' ? styles.subtitleTextAndroid : styles.subtitleText}
          >
            {INTL.message("Submit your feedback - we're listening")}
          </Label>
        </View>
      </View>
    );
  }

  _renderContent(): React.ReactNode {
    return (
      <View style={styles.containerStyle}>
        {this._renderHeader()}
        {this._renderStars()}
        {this._renderInstructionText()}
        {this._renderInputSection()}
        {this._renderButtons()}
      </View>
    );
  }

  render(): React.ReactNode {
    SKZLayoutAnimations.easeInEaseOut();
    if (!this.state.userId) {
      return <View />;
    }

    return (
      <View style={[FTUEStyles.fullScreenContainer, styles.centerDialog]}>
        <View
          style={[
            FTUEStyles.contentBox,
            {borderRadius: 11, overflow: 'hidden', paddingTop: 0, paddingBottom: 0},
          ]}
        >
          {this._renderContent()}
        </View>
      </View>
    );
  }
}
